import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as classesApi from '@/features/classes/api';
import type {
  AssignTeacherInput,
  ClassStudentView,
  ClassTeacherView,
  ClassView,
  CreateClassInput,
  EnrollStudentInput,
  TeacherRosterView,
  UpdateClassInput,
} from '@/features/classes/types';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { ApiError } from '@/lib/api/client';
import type { PagedData } from '@/lib/api/types';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

/**
 * Tenant-scoped query keys embed the Active Organization id AND the
 * relevant class id (ADR-026 Addendum, AO-9) - Org A's cached classes
 * cannot structurally satisfy an Org B query, and switching classes can
 * never leave a stale roster visible.
 */
const classesListKey = (
  organizationId: string | null,
  page: number,
  limit: number,
  learningSpaceId: string | undefined,
) =>
  [
    TENANT_QUERY_PREFIX,
    organizationId,
    'classes',
    'list',
    page,
    limit,
    learningSpaceId ?? null,
  ] as const;
const classDetailKey = (organizationId: string | null, id: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'classes', 'detail', id] as const;
const teacherRosterKey = (organizationId: string | null, page: number, limit: number) =>
  [TENANT_QUERY_PREFIX, organizationId, 'teachers', 'roster', page, limit] as const;
const classTeachersKey = (organizationId: string | null, classId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'classes', classId, 'teachers'] as const;
const classStudentsKey = (organizationId: string | null, classId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'classes', classId, 'students'] as const;

export function useClasses(
  page = 1,
  limit = 20,
  learningSpaceId?: string,
): UseQueryResult<PagedData<ClassView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: classesListKey(activeOrganizationId, page, limit, learningSpaceId),
    queryFn: () => classesApi.listClasses(page, limit, learningSpaceId),
    enabled: activeOrganizationId !== null,
  });
}

export function useClass(id: string): UseQueryResult<ClassView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: classDetailKey(activeOrganizationId, id),
    queryFn: () => classesApi.getClass(id),
    enabled: activeOrganizationId !== null,
  });
}

export function useCreateClass(): UseMutationResult<ClassView, ApiError, CreateClassInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: CreateClassInput) => classesApi.createClass(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'classes', 'list'],
      });
    },
  });
}

export function useUpdateClass(
  id: string,
): UseMutationResult<ClassView, ApiError, UpdateClassInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: UpdateClassInput) => classesApi.updateClass(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: classDetailKey(activeOrganizationId, id) });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'classes', 'list'],
      });
    },
  });
}

export function useDeactivateClass(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (id: string) => classesApi.deactivateClass(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'classes'],
      });
    },
  });
}

export function useTeacherRoster(
  page = 1,
  limit = 100,
): UseQueryResult<PagedData<TeacherRosterView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: teacherRosterKey(activeOrganizationId, page, limit),
    queryFn: () => classesApi.listTeacherRoster(page, limit),
    enabled: activeOrganizationId !== null,
  });
}

export function useClassTeachers(
  classId: string,
): UseQueryResult<readonly ClassTeacherView[], ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: classTeachersKey(activeOrganizationId, classId),
    queryFn: () => classesApi.listClassTeachers(classId),
    enabled: activeOrganizationId !== null,
  });
}

export function useAssignTeacher(
  classId: string,
): UseMutationResult<ClassTeacherView, ApiError, AssignTeacherInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: AssignTeacherInput) => classesApi.assignTeacher(classId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: classTeachersKey(activeOrganizationId, classId),
      });
    },
  });
}

export function useRemoveTeacher(classId: string): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (teacherId: string) => classesApi.removeTeacher(classId, teacherId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: classTeachersKey(activeOrganizationId, classId),
      });
    },
  });
}

export function useClassStudents(
  classId: string,
): UseQueryResult<readonly ClassStudentView[], ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: classStudentsKey(activeOrganizationId, classId),
    queryFn: () => classesApi.listClassStudents(classId),
    enabled: activeOrganizationId !== null,
  });
}

export function useEnrollStudent(
  classId: string,
): UseMutationResult<ClassStudentView, ApiError, EnrollStudentInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: EnrollStudentInput) => classesApi.enrollStudent(classId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: classStudentsKey(activeOrganizationId, classId),
      });
    },
  });
}

export function useRemoveStudent(classId: string): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (studentId: string) => classesApi.removeStudent(classId, studentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: classStudentsKey(activeOrganizationId, classId),
      });
    },
  });
}
